import { useTranslation } from "react-i18next";
import { Linkedin, ArrowUpRight } from "lucide-react";
import { Link, useLocation } from "@tanstack/react-router";
import { SITE } from "@/lib/site";
import { detectLangFromPath, path } from "@/lib/site";
import { Reveal } from "../Reveal";
import { Breadcrumbs } from "../Breadcrumbs";
import { RelatedLinks } from "../RelatedLinks";
import { Marquee } from "../Marquee";
import { MagneticButton } from "../MagneticButton";
import alexandre from "@/assets/alexandre.jpg";

export function AboutPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const lang = detectLangFromPath(location.pathname);
  const journey = t("about.journey", { returnObjects: true }) as { y: string; t: string }[];
  const values = t("about.values", { returnObjects: true }) as { t: string; d: string }[];

  return (
    <>
      <Breadcrumbs lang={lang} current="about" />
      {/* HERO */}
      <section className="relative overflow-hidden border-b-[1.5px] border-foreground">
        <div className="grain" aria-hidden />
        <div
          aria-hidden
          className="hatch float-y pointer-events-none absolute -top-10 -right-10 h-48 w-48 opacity-20 md:h-72 md:w-72"
        />
        <div className="container-page relative pt-14 pb-16 md:pt-20 md:pb-24">
          <Reveal>
            <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-foreground/60">
              / About — {SITE.founder}
            </span>
          </Reveal>
          <div className="mt-8 grid gap-10 lg:grid-cols-12 lg:gap-12">
            <Reveal delay={0.08} className="lg:col-span-8">
              <h1 className="font-display whitespace-pre-line font-black uppercase leading-[1.05] tracking-[-0.045em] text-foreground text-[clamp(2.75rem,8.5vw,7.5rem)] my-0 py-0">
                {t("about.h1")}
              </h1>
            </Reveal>
            <Reveal delay={0.14} className="lg:col-span-4">
              <div className="bento-tile h-full p-6 md:p-7">
                <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-foreground/60">
                  / Brief
                </span>
                <p className="mt-4 text-base leading-relaxed text-foreground/85">
                  {t("about.tldr")}
                </p>
                <p className="mt-5 text-sm text-foreground/70">{t("about.sub")}</p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* PORTRAIT + BIO */}
      <section className="section border-b-[1.5px] border-foreground bg-[var(--surface)]">
        <div className="container-page grid items-start gap-10 sm:grid-cols-12 sm:gap-12">
          <Reveal className="col-span-5 sm:col-span-4 lg:col-span-3">
            <div className="relative">
              <div className="aspect-[4/5] overflow-hidden border-[1.5px] border-foreground">
                <img
                  src={alexandre}
                  alt={`Portrait — ${SITE.founder}, founder of ${SITE.name}`}
                  className="h-full w-full object-cover grayscale"
                  loading="lazy"
                />
              </div>
              <span
                aria-hidden
                className="absolute -bottom-3 -right-3 h-12 w-12 bg-blaze border-[1.5px] border-foreground"
              />
            </div>
          </Reveal>
          <Reveal delay={0.08} className="col-span-7 sm:col-span-8 lg:col-span-9">
            <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-foreground/60">
              / {SITE.founder} — Founder
            </span>
            <p className="mt-6 whitespace-pre-line text-base leading-[1.7] text-foreground/85 md:text-lg">
              {t("about.bio")}
            </p>
            <a
              href={SITE.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-ghost-ink mt-8 inline-flex items-center gap-2 px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.12em]"
            >
              <Linkedin size={14} /> {t("common.followLinkedIn")}
            </a>
          </Reveal>
        </div>
      </section>

      <Marquee
        variant="ink"
        items={[
          "15 years on the floor",
          "Belgium · Luxembourg",
        ]}
      />

      {/* JOURNEY — Numbered bento strip on ink */}
      <section className="section border-b-[1.5px] border-foreground bg-foreground text-background">
        <div className="container-page">
          <Reveal>
            <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-background/60">
              / Journey
            </span>
            <h2 className="font-display mt-3 max-w-3xl text-4xl font-black uppercase leading-[0.95] tracking-[-0.035em] md:text-7xl">
              {t("about.journeyTitle")}
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {journey.map((j, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <div className="border-[1.5px] border-background bg-foreground p-7 h-full transition-colors hover:bg-background hover:text-foreground">
                  <div className="flex items-center justify-between">
                    <span className="font-display text-5xl font-black tracking-[-0.04em]">
                      {String(journey.length - i).padStart(2, "0")}
                    </span>
                    <span className="h-2 w-2 bg-blaze" aria-hidden />
                  </div>
                  <p className="font-mono mt-8 text-[11px] font-bold uppercase tracking-[0.18em] opacity-70">
                    {j.y}
                  </p>
                  <h3 className="font-display mt-2 text-lg font-bold uppercase leading-[1.15] tracking-[-0.01em]">
                    {j.t}
                  </h3>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHY 2AM — oversized manifesto */}
      <section className="relative overflow-hidden border-b-[1.5px] border-foreground bg-background py-20 md:py-32">
        <div
          aria-hidden
          className="hatch-blaze pointer-events-none absolute inset-0 opacity-15"
        />
        <div className="container-page relative">
          <Reveal>
            <div className="flex items-center gap-3">
              <span className="h-2.5 w-2.5 bg-blaze" aria-hidden />
              <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-foreground/60">
                / {t("about.whyTitle")}
              </span>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-display mt-8 font-black uppercase leading-[0.9] tracking-[-0.045em] text-foreground text-[clamp(2.5rem,8vw,7rem)]">
              {t("about.whyTitle")}
            </h2>
          </Reveal>
          <Reveal delay={0.18}>
            <p className="mt-10 max-w-3xl whitespace-pre-line text-lg leading-relaxed text-foreground/80 md:text-xl">
              {t("about.why")}
            </p>
          </Reveal>
        </div>
      </section>

      {/* VALUES — bento mosaic */}
      <section className="section border-b-[1.5px] border-foreground">
        <div className="container-page">
          <Reveal>
            <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-foreground/60">
              / {t("about.valuesTitle")} — {String(values.length).padStart(2, "0")}
            </span>
            <h2 className="font-display mt-3 text-4xl font-black uppercase leading-[0.95] tracking-[-0.035em] md:text-7xl">
              {t("about.valuesTitle")}
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-4 md:grid-cols-3 md:gap-5">
            {values.map((v, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <div
                  className={`bento-tile ${i === 1 ? "is-spark" : i === 2 ? "is-ink" : ""} tile-fx is-hover group flex h-full flex-col p-7 md:p-8`}
                >
                  <span className="font-mono text-xs font-semibold uppercase tracking-[0.18em] opacity-60">
                    0{i + 1} / 0{values.length}
                  </span>
                  <h3 className="font-display mt-8 text-2xl font-black uppercase leading-[1] tracking-[-0.03em] md:text-3xl">
                    {v.t}
                  </h3>
                  <p className="mt-4 text-[14px] leading-relaxed opacity-80">{v.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <div className="mt-12">
              <MagneticButton>
                <Link
                  to={path(lang, "services")}
                  className="btn-blaze inline-flex items-center gap-2 px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.12em]"
                >
                  {t("about.servicesCta")} <ArrowUpRight size={14} />
                </Link>
              </MagneticButton>
            </div>
          </Reveal>
        </div>
      </section>
      <RelatedLinks lang={lang} items={["services", "references", "tools"]} />
    </>
  );
}
